/**
 * ROI Dashboard — KPIs · funnel · channel breakdown · weekly chart · cost-per-WON.
 * Pure SVG charts (no external chart library) to keep bundle lean.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TrendingUp, DollarSign, Target, Users, Coins, Award, RefreshCw, Loader2 } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Logo from "../components/Logo";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v || 0);
const fmtInt = (v) => new Intl.NumberFormat("en-US").format(Math.round(v || 0));
const fmtPct = (v) => `${(v || 0).toFixed(1)}%`;

const COPY = {
  pt: {
    title: "Dashboard de ROI",
    subtitle: "Créditos consumidos × conversão × receita estimada · janela ajustável",
    weeks: "Janela (semanas)",
    deal_value: "Valor médio por WON ($)",
    refresh: "Atualizar",
    kpi_total: "Leads gerados", kpi_contacted: "Contactados", kpi_won: "WON", kpi_revenue: "Receita estimada",
    kpi_credits: "Créditos consumidos", kpi_cpw: "Custo / WON", kpi_conv: "Conversão",
    kpi_replied: "Respostas detectadas",
    funnel: "Funil de conversão",
    by_channel: "Breakdown por canal",
    weekly: "Tendência semanal",
    no_data: "Ainda sem dados — gere alguns leads e WON para popular o ROI.",
    revenue_chart: "Receita estimada (linha) × Leads (barras)",
    leads_label: "Leads", won_label: "WON", channel_label: "Canal", conv_label: "Conv.",
    revenue_label: "Receita", week_label: "Semana",
  },
  en: {
    title: "ROI Dashboard",
    subtitle: "Credits used × conversion × estimated revenue · tunable window",
    weeks: "Window (weeks)",
    deal_value: "Avg deal value ($)",
    refresh: "Refresh",
    kpi_total: "Leads generated", kpi_contacted: "Contacted", kpi_won: "WON", kpi_revenue: "Est. revenue",
    kpi_credits: "Credits used", kpi_cpw: "Cost / WON", kpi_conv: "Conversion",
    kpi_replied: "Replies detected",
    funnel: "Conversion funnel",
    by_channel: "By channel",
    weekly: "Weekly trend",
    no_data: "No data yet — generate leads and WONs to populate ROI.",
    revenue_chart: "Estimated revenue (line) × Leads (bars)",
    leads_label: "Leads", won_label: "WON", channel_label: "Channel", conv_label: "Conv.",
    revenue_label: "Revenue", week_label: "Week",
  },
};

function KpiCard({ label, value, icon: Icon, accent = "#8B5CF6", testid }) {
  return (
    <div className="al-card p-4" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--al-text-muted)]">{label}</div>
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: `${accent}1A`, color: accent }}>
          <Icon className="w-3.5 h-3.5" />
        </div>
      </div>
      <div className="text-[24px] font-extrabold mt-2 leading-none tracking-tight">{value}</div>
    </div>
  );
}

function Funnel({ items, t, accent = "#8B5CF6" }) {
  const max = Math.max(1, ...items.map((s) => s.count));
  return (
    <div className="al-card p-5">
      <h3 className="text-[14px] font-bold mb-3">{t.funnel}</h3>
      <div className="space-y-2" data-testid="roi-funnel">
        {items.map((s) => {
          const w = (s.count / max) * 100;
          return (
            <div key={s.stage} className="flex items-center gap-3" data-testid={`roi-funnel-${s.stage}`}>
              <div className="w-24 text-[12px] font-bold tracking-wide">{s.stage}</div>
              <div className="flex-1 h-7 rounded-md bg-[var(--al-border)] overflow-hidden">
                <div className="h-full rounded-md flex items-center px-2 text-white text-[11px] font-bold" style={{ width: `${w}%`, backgroundColor: accent, minWidth: w > 0 ? 32 : 0 }}>
                  {s.count > 0 ? s.count : ""}
                </div>
              </div>
              <div className="w-14 text-right text-[12px] font-mono">{s.count}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChannelTable({ rows, t }) {
  return (
    <div className="al-card p-5">
      <h3 className="text-[14px] font-bold mb-3">{t.by_channel}</h3>
      <div className="overflow-x-auto" data-testid="roi-by-channel">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-[var(--al-text-muted)] border-b border-[var(--al-border)]">
              <th className="py-2">{t.channel_label}</th>
              <th className="py-2 text-right">{t.leads_label}</th>
              <th className="py-2 text-right">{t.won_label}</th>
              <th className="py-2 text-right">{t.conv_label}</th>
              <th className="py-2 text-right">{t.revenue_label}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="py-6 text-center text-[var(--al-text-muted)] italic">{t.no_data}</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.channel} className="border-b border-[var(--al-border)]/50" data-testid={`roi-channel-row-${r.channel}`}>
                <td className="py-2 font-bold">{r.channel}</td>
                <td className="py-2 text-right font-mono">{fmtInt(r.leads)}</td>
                <td className="py-2 text-right font-mono">{fmtInt(r.won)}</td>
                <td className="py-2 text-right font-mono">{fmtPct(r.conversion)}</td>
                <td className="py-2 text-right font-mono">{fmtMoney(r.revenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WeeklyChart({ data, t }) {
  const W = 720;
  const H = 220;
  const PAD = { l: 40, r: 40, t: 16, b: 28 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const maxLeads = Math.max(1, ...data.map((d) => d.leads));
  const maxRevenue = Math.max(1, ...data.map((d) => d.revenue));
  const barW = data.length ? innerW / data.length * 0.6 : 0;
  const stepX = data.length ? innerW / data.length : 0;
  const linePoints = data.map((d, i) => {
    const x = PAD.l + i * stepX + stepX / 2;
    const y = PAD.t + innerH - (d.revenue / maxRevenue) * innerH;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  return (
    <div className="al-card p-5">
      <h3 className="text-[14px] font-bold mb-1">{t.weekly}</h3>
      <p className="text-[11.5px] text-[var(--al-text-muted)] mb-3">{t.revenue_chart}</p>
      <div className="overflow-x-auto" data-testid="roi-weekly-chart">
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="none" style={{ minWidth: 480 }}>
          {/* y-axis grid lines */}
          {[0, 0.5, 1].map((p) => (
            <line key={`grid-${p}`} x1={PAD.l} x2={W - PAD.r}
              y1={PAD.t + innerH * (1 - p)} y2={PAD.t + innerH * (1 - p)}
              stroke="var(--al-border)" strokeDasharray="3 4" strokeWidth="1" />
          ))}
          {/* leads bars */}
          {data.map((d, i) => {
            const h = (d.leads / maxLeads) * innerH;
            const x = PAD.l + i * stepX + (stepX - barW) / 2;
            const y = PAD.t + innerH - h;
            return (
              <g key={d.week_start}>
                <rect x={x} y={y} width={barW} height={h}
                  fill="#8B5CF6" opacity={0.85} rx="2"
                  data-testid={`roi-week-bar-${d.week_start}`} />
                {data.length <= 16 && (
                  <text x={x + barW / 2} y={H - PAD.b + 14}
                    fontSize="9" fill="var(--al-text-muted)" textAnchor="middle">
                    {d.week_start.slice(5)}
                  </text>
                )}
              </g>
            );
          })}
          {/* revenue line */}
          {data.length > 1 && (
            <polyline points={linePoints} fill="none" stroke="#10B981" strokeWidth="2.5" />
          )}
          {data.map((d, i) => {
            const x = PAD.l + i * stepX + stepX / 2;
            const y = PAD.t + innerH - (d.revenue / maxRevenue) * innerH;
            return <circle key={`pt-${d.week_start}`} cx={x} cy={y} r="3" fill="#10B981" />;
          })}
          {/* axis labels */}
          <text x={PAD.l - 6} y={PAD.t + 8} fontSize="9" fill="var(--al-text-muted)" textAnchor="end">{t.leads_label}/{t.revenue_label}</text>
          <text x={PAD.l - 6} y={PAD.t + innerH} fontSize="9" fill="var(--al-text-muted)" textAnchor="end">0</text>
        </svg>
      </div>
      <div className="flex items-center gap-4 mt-2 text-[11px] text-[var(--al-text-muted)]">
        <div className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#8B5CF6]/85" /> {t.leads_label}</div>
        <div className="flex items-center gap-1"><span className="w-3 h-1 bg-[#10B981]" /> {t.revenue_label}</div>
      </div>
    </div>
  );
}

export default function ROI() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const t = COPY[isPt ? "pt" : "en"];

  const [weeks, setWeeks] = useState(12);
  const [dealValue, setDealValue] = useState(2500);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/roi/summary`, { params: { weeks, default_deal_value: dealValue } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [weeks, dealValue]);

  useEffect(() => { load(); }, [load]);

  const k = data?.kpis || {};
  const funnel = data?.funnel || [];
  const byChannel = data?.by_channel || [];
  const weekly = data?.weekly || [];

  return (
    <div className="min-h-screen flex" style={{ background: "var(--al-bg)" }} data-testid="roi-page">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <header className="h-14 border-b border-[var(--al-border)] flex items-center justify-between px-6 bg-[var(--al-card)]">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="text-[12px] text-[var(--al-text-muted)] hidden md:inline">·</span>
            <h1 className="text-[14px] font-bold tracking-tight">{t.title}</h1>
          </div>
          <div className="flex items-center gap-2"><LangToggle /><ThemeToggle /></div>
        </header>

        <div className="p-6 space-y-5 max-w-[1480px] w-full mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-[22px] font-extrabold tracking-tight">{t.title}</h2>
              <p className="text-[12.5px] text-[var(--al-text-muted)] mt-1">{t.subtitle}</p>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-[11px] font-bold uppercase text-[var(--al-text-muted)]">
                {t.weeks}
                <input type="number" min={1} max={52} value={weeks} onChange={(e) => setWeeks(Number(e.target.value) || 12)}
                  data-testid="roi-weeks" className="al-input h-8 w-16 ml-2 text-[12px] inline-block" />
              </label>
              <label className="text-[11px] font-bold uppercase text-[var(--al-text-muted)]">
                {t.deal_value}
                <input type="number" min={0} value={dealValue} onChange={(e) => setDealValue(Number(e.target.value) || 0)}
                  data-testid="roi-deal-value" className="al-input h-8 w-24 ml-2 text-[12px] inline-block" />
              </label>
              <button type="button" onClick={load} disabled={loading}
                data-testid="roi-refresh"
                className="h-8 px-3 rounded-md bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-[11px] font-bold disabled:opacity-50 inline-flex items-center gap-1.5">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {t.refresh}
              </button>
            </div>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <KpiCard label={t.kpi_total}     value={fmtInt(k.total_leads)}       icon={Users}      accent="#8B5CF6" testid="roi-kpi-total" />
            <KpiCard label={t.kpi_contacted} value={fmtInt(k.contacted)}         icon={Target}     accent="#0EA5E9" testid="roi-kpi-contacted" />
            <KpiCard label={t.kpi_won}       value={fmtInt(k.won)}               icon={Award}      accent="#10B981" testid="roi-kpi-won" />
            <KpiCard label={t.kpi_revenue}   value={fmtMoney(k.revenue_estimated)} icon={DollarSign} accent="#10B981" testid="roi-kpi-revenue" />
            <KpiCard label={t.kpi_conv}      value={fmtPct(k.conversion_rate)}   icon={TrendingUp} accent="#F59E0B" testid="roi-kpi-conv" />
            <KpiCard label={t.kpi_credits}   value={fmtInt(k.credits_used)}      icon={Coins}      accent="#F22F46" testid="roi-kpi-credits" />
            <KpiCard label={t.kpi_cpw}       value={fmtMoney(k.avg_cost_per_won)} icon={DollarSign} accent="#F22F46" testid="roi-kpi-cpw" />
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            <Funnel items={funnel} t={t} />
            <ChannelTable rows={byChannel} t={t} />
          </div>

          <WeeklyChart data={weekly} t={t} />
        </div>
      </div>
    </div>
  );
}
